import { Share, Plus, X, MoreVertical } from "lucide-react";
import { useI18n } from "@/context/I18n";

/**
 * Step by step sheet for adding SONLYO to an iPhone home screen.
 *
 * iOS exposes no install API at all: there is no `beforeinstallprompt` and no
 * way to open the Share sheet from script, so a button can only show where it
 * is. Since iOS 16.4 Chrome, Edge and Firefox can add to the home screen from
 * their own share menus; before that only Safari could, which is why the
 * browser is called out explicitly.
 */
export function isIosSafari() {
  const ua = window.navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  if (!isIos) return false;
  // Every iOS browser reports Safari in its UA; the others add their own token.
  return !/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua);
}

export function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/**
 * Install steps for whichever platform the reader is on.
 *
 * Chrome only fires its install prompt once, and not at all if the app is
 * already installed, so an "install" button often has nothing to trigger.
 * Telling a Chrome user their browser cannot install apps is simply wrong: the
 * browser menu always can.
 */
export default function IosInstallSheet({ onClose }) {
  const { t } = useI18n();
  const ios = isIos();
  const safari = isIosSafari();

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ios-install-sheet"
    >
      <div
        className="w-full max-w-md bg-[#141414] border-t border-white/10 rounded-t-2xl p-5 pb-[calc(20px+env(safe-area-inset-bottom))] animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <img src="/apple-touch-icon.png" alt="" className="w-12 h-12 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold">{t("install_title")}</h3>
            <p className="text-sm text-zinc-400">{ios ? t("ios_sheet_sub") : t("chrome_sheet_sub")}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-zinc-500 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {ios && !safari && (
          <p className="text-xs bg-[#FF2A4D]/15 border border-[#FF2A4D]/30 text-zinc-200 rounded-lg px-3 py-2 mb-4">
            {t("ios_use_safari")}
          </p>
        )}

        <ol className="space-y-3 text-sm">
          <li className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">1</span>
            <span className="flex items-center gap-1.5 flex-wrap">
              {ios ? t("ios_step_1") : t("chrome_step_1")}
              {ios ? <Share className="w-4 h-4 text-[#FF2A4D]" /> : <MoreVertical className="w-4 h-4 text-[#FF2A4D]" />}
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">2</span>
            <span className="flex items-center gap-1.5 flex-wrap">
              {ios ? t("ios_step_2") : t("chrome_step_2")}
              {ios ? <Plus className="w-4 h-4 text-[#FF2A4D]" /> : null}
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">3</span>
            <span>{ios ? t("ios_step_3") : t("chrome_step_3")}</span>
          </li>
        </ol>

        <p className="text-xs text-zinc-500 mt-4 leading-relaxed">{t("ios_note")}</p>
      </div>
    </div>
  );
}
